import { authCopy } from '../copy'
import { useAuthStore } from '../store/auth'
import type { AcceptInviteResponse, LoginResponse } from '../types/api'
import { ApiError, api } from './client'

// login uses raw fetch: a 401 (wrong password) must not trigger the api
// wrapper's logout-and-redirect side-effect.
export async function login(
  username: string,
  password: string,
): Promise<LoginResponse> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  if (!res.ok)
    throw new Error(
      res.status === 401
        ? authCopy.login.wrongCredentials
        : authCopy.login.failed,
    )
  return res.json()
}

// refreshToken uses raw fetch for the same reason as login: a 401 on the
// refresh endpoint must not re-trigger the wrapper's logout side-effect.
export async function refreshToken(): Promise<LoginResponse> {
  const token = useAuthStore.getState().token
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  if (!res.ok) throw new Error('Token refresh failed')
  return res.json() as Promise<LoginResponse>
}

export async function forgotPassword(email: string): Promise<void> {
  await api.post('/api/auth/forgot-password', { email })
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<void> {
  try {
    await api.post('/api/auth/reset-password', {
      token,
      new_password: newPassword,
    })
  } catch (e) {
    if (e instanceof ApiError && e.status === 400)
      throw new Error(authCopy.reset.invalidToken, { cause: e })
    if (e instanceof ApiError && e.status === 422)
      throw new Error(authCopy.reset.incomplete, { cause: e })
    throw e
  }
}

// acceptInvite uses raw fetch: a 400/422 from a bad token must not trigger the
// api wrapper's logout-and-redirect side-effect.
export async function acceptInvite(
  token: string,
  username: string,
  password: string,
): Promise<AcceptInviteResponse> {
  const res = await fetch('/api/auth/accept-invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, username, password }),
  })
  if (!res.ok) {
    if (res.status === 409) throw new Error(authCopy.invite.usernameTaken)
    if (res.status === 400) throw new Error(authCopy.invite.invalid)
    if (res.status === 422) throw new Error(authCopy.invite.incomplete)
    throw new Error(authCopy.invite.failed)
  }
  return res.json()
}
